import React, { forwardRef, MutableRefObject, useMemo, useState } from 'react';
import {
  Box,
  Card,
  SkeletonGroup,
  SkeletonLine,
  Table,
  TableProps,
} from '@wix/design-system';

export interface TableSkeletonProps {
  dataHook?: string;
  rowsCount?: number;
  columnsCount?: number;
  rowVerticalPadding?: TableProps['rowVerticalPadding'];
}

export function TableSkeleton(props: TableSkeletonProps) {
  const { dataHook, rowsCount = 7, columnsCount = 4, ...rest } = props;

  const data = useMemo(
    () =>
      new Array(rowsCount)
        .fill(null)
        .map((_, i) => ({ id: `skeleton-row-${i}` })),
    [rowsCount],
  );

  const columns = useMemo(
    () =>
      new Array(columnsCount).fill(null).map((_, i) => ({
        title: '',
        key: `skeleton-column-${i}`,
        dataHook: `skeleton-column-${i}`,
        render: () => (
          <Box direction="vertical" width="100%" maxWidth="192px">
            <SkeletonGroup>
              <SkeletonLine width="100%" marginTop="SP1" marginBottom="SP1" />
            </SkeletonGroup>
          </Box>
        ),
      })),
    [columnsCount],
  );

  const [TrElementType] = useState(() =>
    forwardRef(
      (
        { rowData: _rowData, ...trProps }: { rowData?: unknown },
        ref:
          | ((instance: HTMLTableRowElement | null) => void)
          | MutableRefObject<HTMLTableRowElement | null>
          | null,
      ) => {
        // <TableSkeleton/> is rendered inside the main <Table/> component, so we need to remove the data-table-row attribute from the <tr/> element to make testkit not to consider skeleton rows as real rows
        return <tr ref={ref} {...trProps} data-table-row={undefined} />;
      },
    ),
  );

  return (
    // <Box/> wrapper always aligns the skeleton to the top of the table
    <Box marginBottom="auto" dataHook={dataHook}>
      <Table
        skin="neutral"
        data={data}
        columns={columns}
        withWrapper={false}
        {...{ TrElementType }}
        {...rest}
      >
        <Card>
          <Table.Content titleBarVisible={true} />
        </Card>
      </Table>
    </Box>
  );
}
